import { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Fab,
  Stack,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { MdQrCode2 } from "react-icons/md";
import CodeScannerDialog from "components/utils/CodeScannerDialog";
import { ROUTES } from "constants/index";
import type { IMovementWorkOrderListItem } from "types";
import { useFractionationOrders } from "providers/FractionationOrdersProvider";

interface FractionationScannerProps {
  disabled?: boolean;
  aboveMobileFooter?: boolean;
}

export default function FractionationScanner({
  disabled = false,
  aboveMobileFooter = false,
}: FractionationScannerProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { orders, loading, reloadOrders } = useFractionationOrders();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [matches, setMatches] = useState<IMovementWorkOrderListItem[]>([]);
  const [scannedLot, setScannedLot] = useState("");

  const openBundle = (order: IMovementWorkOrderListItem, lot: string) => {
    setMatches([]);
    setOpen(false);
    const path = ROUTES.FRACTIONATION_DETAIL.replace(":id", String(order.id));
    navigate(path, {
      replace: location.pathname === path,
      state: { scannedLot: lot },
    });
  };

  const findBundle = (lot: string) => {
    setError("");
    if (!orders) {
      setError("No se pudieron cargar las órdenes. Reintentá la carga.");
      return;
    }
    const found = orders.filter((order) => order.lotes_externos.includes(lot));
    if (found.length === 0) {
      setError(`No hay un fardo con lote externo ${lot} en las órdenes.`);
    } else if (found.length === 1) {
      openBundle(found[0], lot);
    } else {
      setScannedLot(lot);
      setOpen(false);
      setMatches(found);
    }
  };

  return (
    <>
      <Fab
        color="primary"
        aria-label="Escanear fardo"
        disabled={disabled}
        onClick={() => {
          setError("");
          setOpen(true);
          if (!orders && !loading) void reloadOrders();
        }}
        sx={{
          position: "fixed",
          right: 24,
          bottom: aboveMobileFooter
            ? { xs: "calc(96px + env(safe-area-inset-bottom))", md: 24 }
            : 24,
        }}
      >
        <MdQrCode2 size={30} />
      </Fab>
      <CodeScannerDialog
        open={open}
        error={loading && !orders ? "Cargando órdenes…" : error}
        onRetry={() => {
          setError("");
          if (!orders && !loading) void reloadOrders();
        }}
        onClose={() => setOpen(false)}
        onDetected={findBundle}
      />
      <Dialog
        open={matches.length > 0}
        onClose={() => setMatches([])}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Seleccionar orden para {scannedLot}</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            {matches.map((order) => (
              <Button
                key={order.id}
                onClick={() => openBundle(order, scannedLot)}
              >
                Orden #{order.id} · Remito{" "}
                {order.movement.client_document || "Sin documento"}
              </Button>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
